import { useMemo } from "react";
import { useAuth } from "./auth-context";
import type { ClubActivitySummary, ClubSwitch } from "./api-client";

export type ClubFeature = "grades" | "program" | "techniques";

export interface ClubFeatures {
  /** The activities the club offers; null until the signed-in user carries them (older sessions). */
  activities: ClubActivitySummary[] | null;
  /** True when at least one offered activity uses the feature. Permissive until the activities are known. */
  has: (feature: ClubFeature) => boolean;
  /** False when the club switched this part of the app off in its settings (on while unknown). */
  enabled: (feature: ClubSwitch) => boolean;
  /** True when this activity uses the feature. Permissive when the activity or the list is unknown. */
  activityHas: (activityKey: string | null | undefined, feature: ClubFeature) => boolean;
}

/** Display name of an activity key among the club's activities (the key itself, capitalised, when unknown). */
export function useActivityLabel(): (key: string) => string {
  const { activities } = useClubFeatures();
  return (key) => activities?.find((a) => a.key === key.toLowerCase())?.name ?? key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
}

/** What the club's activities give, so screens can hide what no activity needs. */
export function useClubFeatures(): ClubFeatures {
  const { user } = useAuth();
  const activities = user?.club?.activities ?? null;
  const switches = user?.club?.features ?? null;

  return useMemo<ClubFeatures>(() => ({
    activities,
    enabled: (feature) => switches?.[feature] !== false,
    has: (feature) => activities === null || activities.some((a) => a.features.includes(feature)),
    activityHas: (key, feature) => {
      if (activities === null || !key) return true;
      const activity = activities.find((a) => a.key === key);
      return activity ? activity.features.includes(feature) : false;
    },
  }), [activities, switches]);
}
